import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Plus, Pencil, Trash2, Loader2, X, Check, AlertCircle, Power, Percent } from "lucide-react";
import {
  getAdminTaxProfiles, createAdminTaxProfile, updateAdminTaxProfile, toggleAdminTaxProfile, deleteAdminTaxProfile,
  AdminTaxProfile,
} from "../../api/services/adminTaxProfileService";
import { getErrorMessage } from "@/utils/errorUtils";

const emptyProfile = (): Partial<AdminTaxProfile> => ({
  country_code: "", label: "", enabled: true, rate_bps: 0, inclusive: false,
  registration_number: "", effective_from: "", effective_to: ""
});

export default function TaxProfiles() {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState<AdminTaxProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<Partial<AdminTaxProfile> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminTaxProfile | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr("");
    try { setProfiles(await getAdminTaxProfiles()); }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to load tax profiles"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(emptyProfile()); setModalOpen(true); };
  const openEdit = (p: AdminTaxProfile) => {
    setEditing({ ...p });
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditing(null); };

  const save = async () => {
    if (!editing) return;
    if (!editing.label || !editing.country_code || editing.rate_bps === undefined || !editing.effective_from) {
      setErr("Label, Country Code, Rate, and Effective From are required"); return;
    }
    setSaving(true); setErr("");
    const payload: Partial<AdminTaxProfile> = {
      ...editing,
      country_code: editing.country_code.toUpperCase(),
      rate_bps: Number(editing.rate_bps),
    };
    try {
      if (editing._id) await updateAdminTaxProfile(editing._id, payload);
      else await createAdminTaxProfile(payload);
      closeModal(); await load();
    } catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to save tax profile"); }
    finally { setSaving(false); }
  };

  const onToggle = async (p: AdminTaxProfile) => {
    if (!p._id) return;
    setBusyId(p._id);
    try { await toggleAdminTaxProfile(p._id); await load(); }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to toggle tax profile"); }
    finally { setBusyId(null); }
  };

  const doDelete = async () => {
    if (!confirmDelete?._id) return;
    setBusyId(confirmDelete._id);
    try { await deleteAdminTaxProfile(confirmDelete._id); setConfirmDelete(null); await load(); }
    catch (e: unknown) { setErr(getErrorMessage(e) || "Failed to delete tax profile"); }
    finally { setBusyId(null); }
  };

  const fld = "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-transparent px-3 py-2 text-sm";

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Percent className="w-6 h-6" /> {t('admin.taxprofiles.title', 'Tax Profiles')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('admin.taxprofiles.description', 'Manage country-specific tax profiles for T-04 pricing.')}</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium">
          <Plus className="w-4 h-4" /> {t('admin.taxprofiles.button.addprofile', 'Add Profile')}
        </button>
      </div>

      {err && (
        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('admin.taxprofiles.loading', 'Loading tax profiles…')}</div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-16 text-gray-500">{t('admin.taxprofiles.empty', 'No tax profiles yet. Click Add Profile to create one.')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('admin.taxprofiles.table.label', 'Label')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.taxprofiles.table.country', 'Country')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.taxprofiles.table.rate', 'Rate')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.taxprofiles.table.inclusive', 'Inclusive?')}</th>
                  <th className="px-4 py-3 font-medium">{t('admin.taxprofiles.table.status', 'Status')}</th>
                  <th className="px-4 py-3 font-medium text-right">{t('admin.taxprofiles.table.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((p) => (
                  <tr key={p._id} className="border-b border-gray-100 dark:border-gray-800/60">
                    <td className="px-4 py-3 font-medium">{p.label}</td>
                    <td className="px-4 py-3">
                      <span className="uppercase font-medium text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{p.country_code}</span>
                    </td>
                    <td className="px-4 py-3">{(p.rate_bps / 100).toFixed(2)}%</td>
                    <td className="px-4 py-3">{p.inclusive ? "Yes" : "No"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${p.enabled ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400" : "bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-400"}`}>
                        {p.enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => onToggle(p)} disabled={busyId === p._id} title={p.enabled ? "Disable" : "Enable"} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50">
                          {busyId === p._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Power className={`w-4 h-4 ${p.enabled ? "text-green-600" : "text-gray-400"}`} />}
                        </button>
                        <button onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setConfirmDelete(p)} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {modalOpen && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-lg font-semibold">{editing._id ? "Edit Tax Profile" : "New Tax Profile"}</h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Label</label>
                  <input className={fld} value={editing.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="e.g. VAT 7.5%" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Country Code (ISO 2-letter)</label>
                  <input className={fld} value={editing.country_code || ""} onChange={(e) => setEditing({ ...editing, country_code: e.target.value.toUpperCase() })} placeholder="e.g. NG, US" maxLength={2} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Rate (bps, e.g. 750 = 7.5%)</label>
                  <input type="number" className={fld} value={editing.rate_bps ?? 0} onChange={(e) => setEditing({ ...editing, rate_bps: e.target.value ? Number(e.target.value) : 0 })} placeholder="e.g. 750" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Registration Number</label>
                  <input className={fld} value={editing.registration_number || ""} onChange={(e) => setEditing({ ...editing, registration_number: e.target.value })} placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Effective From</label>
                  <input type="datetime-local" className={fld} value={editing.effective_from ? new Date(editing.effective_from).toISOString().slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, effective_from: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Effective To</label>
                  <input type="datetime-local" className={fld} value={editing.effective_to ? new Date(editing.effective_to).toISOString().slice(0, 16) : ""} onChange={(e) => setEditing({ ...editing, effective_to: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.inclusive} onChange={(e) => setEditing({ ...editing, inclusive: e.target.checked })} />
                  Inclusive (Tax is part of gross price)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                  Enabled
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-800">
              <button onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700">{t('admin.taxprofiles.button.cancel', 'Cancel')}</button>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-sm shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Delete profile?</h3>
            <p className="text-sm text-gray-500 mb-5">This permanently removes the tax profile. Subscriptions already recorded will retain their snapshot.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-700">{t('admin.taxprofiles.button.cancel', 'Cancel')}</button>
              <button onClick={doDelete} disabled={busyId === confirmDelete._id} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                {busyId === confirmDelete._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
